import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useFlags, useMyTournaments, useProfile, useQualification } from '../api/hooks';
import { Loader } from '../components/StateViews';
import { CosmeticAvatar } from '../components/cosmetics/CosmeticAvatar';
import { NickFx } from '../components/cosmetics/NickFx';
import { ProfileBg } from '../components/cosmetics/ProfileBg';
import { RowFxLayer, rowFxClass } from '../components/cosmetics/RowFx';
import { shortDate, weekday } from '../format';
import { backButton, haptics } from '../telegram';

/** 6.7b — «Как меня видят другие»: профиль глазами другого игрока со всей
 *  надетой косметикой (макет колод): плашка «Предпросмотр» + «Готово»,
 *  строка «В рейтинге отбора» с эффектом, плитки статистики, последние игры. */
export function WardrobePreviewScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const profile = useProfile(null);
  const qual = useQualification();
  const games = useMyTournaments(enabled);
  const navigate = useNavigate();

  useEffect(() => {
    const back = () => navigate('/pass/wardrobe');
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;
  if (!flags.data || profile.isLoading || !profile.data) return <Loader />;
  const data = profile.data;
  const me =
    qual.data?.status === 'active' || qual.data?.status === 'finished' ? qual.data.me : null;
  const recent = (games.data ?? []).slice(0, 3);

  return (
    <div className="bp-wrp">
      <div className="bp-wrp-bg pbg-host">
        <ProfileBg cosmetic={data.cosmetics?.bg} parallax />
      </div>
      {/* Плашка предпросмотра (макет): подпись слева, «Готово» справа */}
      <div className="bp-wrp-top">
        <span className="t">Предпросмотр: так тебя видят</span>
        <button
          className="done"
          onClick={() => {
            haptics.tap();
            navigate('/pass/wardrobe');
          }}
        >
          Готово
        </button>
      </div>
      <div className="bp-wrp-body">
        <CosmeticAvatar name={data.displayName} src={data.photoUrl} size={104} frame={data.cosmetics?.frame} />
        <div className="bp-wrp-nick">
          <NickFx cosmetic={data.cosmetics?.nick}>{data.displayName}</NickFx>
        </div>
        <div className="bp-wrp-stats">
          <div className="tile">
            <span className="v mono">{me && me.rank !== null ? `#${me.rank}` : '—'}</span>
            <span className="l">в отборе</span>
          </div>
          <div className="tile">
            <span className="v mono">{me ? me.rating : '—'}</span>
            <span className="l">баллов</span>
          </div>
          <div className="tile">
            <span className="v mono">{me ? me.gamesPlayed : (games.data ?? []).length}</span>
            <span className="l">игр в сезоне</span>
          </div>
        </div>
        <div className="bp-wrp-cap mono">В РЕЙТИНГЕ ОТБОРА</div>
        <div className={`bp-wrp-row rfx-host${rowFxClass(data.cosmetics?.row)}`}>
          {/* предпросмотр: все эффекты играют циклом */}
          <RowFxLayer cosmetic={data.cosmetics?.row} height={52} mode="loop" />
          <span className="rk mono">{me?.rank ?? '—'}</span>
          <CosmeticAvatar name={data.displayName} src={data.photoUrl} size={34} frame={data.cosmetics?.frame} />
          <span className="nm">
            <NickFx cosmetic={data.cosmetics?.nick}>{data.displayName}</NickFx>
          </span>
          <span className="pts mono">{me?.rating ?? ''}</span>
        </div>
        {recent.length > 0 && (
          <>
            <div className="bp-wrp-cap mono">ПОСЛЕДНИЕ ИГРЫ</div>
            <div className="bp-wrp-games">
              {recent.map((g) => (
                <div className="g" key={g.gameId}>
                  <span className="d">
                    {weekday(g.startTime)} {shortDate(g.startTime)}
                  </span>
                  <span className="p mono">{g.position !== null ? `${g.position}-е место` : '—'}</span>
                  <span className="pt mono">{g.points > 0 ? `+${g.points}` : g.points}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="bp-wrp-note">Так тебя видят в профиле, рейтинге и списках</div>
      </div>
    </div>
  );
}
